import {
  type Backend,
  BackgroundJobName,
  type CannotRecoverConversation,
  type Conversation,
  type ConversationId,
  type ConversationNotFound,
  ConversationStatus,
  type InferenceOptions,
  type InferenceOptionsNotValid,
  type UnexpectedError,
} from "@superego/backend";
import type { ResultPromise } from "@superego/global-types";
import {
  makeSuccessfulResult,
  makeUnsuccessfulResult,
  validateInferenceOptions,
} from "@superego/shared-utils";
import * as v from "valibot";
import type ConversationEntity from "../../entities/ConversationEntity.js";
import UnexpectedAssistantError from "../../errors/UnexpectedAssistantError.js";
import makeConversation from "../../makers/makeConversation.js";
import makeResultError from "../../makers/makeResultError.js";
import * as structuralSchemas from "../../structural-schemas/index.js";
import BackendUsecase from "../../utils/BackendUsecase.js";
import ConversationUtils from "../../utils/ConversationUtils.js";
import isEmpty from "../../utils/isEmpty.js";
import CollectionsList from "../collections/List.js";

export default class AssistantsRecoverConversation extends BackendUsecase<
  Backend["assistants"]["recoverConversation"]
> {
  argumentsSchema = v.tuple([
    structuralSchemas.backend.ids.conversationId(),
    structuralSchemas.backend.types.inferenceOptions("completion"),
  ]);
  resultSchema = structuralSchemas.global.result(
    structuralSchemas.backend.types.conversation(),
    [
      structuralSchemas.backend.errors.cannotRecoverConversation(),
      structuralSchemas.backend.errors.conversationNotFound(),
      structuralSchemas.backend.errors.inferenceOptionsNotValid(),
      structuralSchemas.backend.errors.unexpectedError(),
    ],
  );

  async exec(
    id: ConversationId,
    inferenceOptions: InferenceOptions<"completion">,
  ): ResultPromise<
    Conversation,
    | ConversationNotFound
    | CannotRecoverConversation
    | InferenceOptionsNotValid
    | UnexpectedError
  > {
    const globalSettings = await this.repos.globalSettings.get();

    const inferenceOptionsIssues = validateInferenceOptions(
      inferenceOptions,
      globalSettings.inference,
    );
    if (!isEmpty(inferenceOptionsIssues)) {
      return makeUnsuccessfulResult(
        makeResultError("InferenceOptionsNotValid", {
          issues: inferenceOptionsIssues,
        }),
      );
    }

    const conversation = await this.repos.conversation.find(id);
    if (!conversation) {
      return makeUnsuccessfulResult(
        makeResultError("ConversationNotFound", { conversationId: id }),
      );
    }

    const { data: collections } = await this.sub(CollectionsList).exec(false);
    if (!collections) {
      throw new UnexpectedAssistantError("Getting collections failed.");
    }
    const contextFingerprint =
      await ConversationUtils.getContextFingerprint(collections);
    const isStuckProcessing =
      conversation.status === ConversationStatus.Processing &&
      Date.now() - conversation.processingStartedAt.getTime() >=
        this.config.conversationProcessingStuckTimeout;
    const canBeRecovered =
      (isStuckProcessing || conversation.status === ConversationStatus.Error) &&
      conversation.contextFingerprint === contextFingerprint;
    if (!canBeRecovered) {
      return makeUnsuccessfulResult(
        makeResultError("CannotRecoverConversation", {
          conversationId: id,
          reason:
            conversation.status === ConversationStatus.Idle
              ? "ConversationIsIdle"
              : conversation.contextFingerprint !== contextFingerprint
                ? "ConversationHasOutdatedContext"
                : "ConversationIsProcessing",
        }),
      );
    }

    const updatedConversation: ConversationEntity = {
      ...conversation,
      status: ConversationStatus.Processing,
      processingStartedAt: new Date(),
      error: null,
    };
    await this.repos.conversation.upsert(updatedConversation);

    await this.enqueueBackgroundJob({
      name: BackgroundJobName.ProcessConversation,
      input: { id, inferenceOptions },
    });

    return makeSuccessfulResult(
      makeConversation(updatedConversation, contextFingerprint),
    );
  }
}
